import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import IndexPage from 'flarum/forum/components/IndexPage';
import PostsUserPage from 'flarum/forum/components/PostsUserPage';
import LinkButton from 'flarum/common/components/LinkButton';
import FieldSet from 'flarum/common/components/FieldSet';
import UpcomingEvents from './components/UpcomingEvents';
import ActivityHeatmap from './components/ActivityHeatmap';
import PulseWidget from './components/PulseWidget';
import OnThisDayWidget from './components/OnThisDayWidget';
import CelebrationsWidget from './components/CelebrationsWidget';
import BirthdayField from './components/BirthdayField';
import { registerIntegrations } from './integrations';
import registerFofWidget, { hasFofWidgets } from '../common/registerFofWidget';
import { peek } from '../common/widgetData';
import { startCountdowns } from './countdowns';
/**
 * Forum entry point.
 *  - Registers the /calendar route + a nav link.
 *  - On stock Flarum, mounts the "Upcoming events" widget in the index sidebar
 *    (no theme required). Bespoke / Page Builder get a proper widget/block via
 *    registerIntegrations().
 */
app.initializers.add('ernestdefoe/calendar', () => {
  app.routes.calendar = {
    path: '/calendar',
    component: () => import(/* webpackChunkName: 'forum/components/CalendarPage', webpackMode: 'lazy-once' */ './components/CalendarPage')
  };

  // Nav link sits with "All Discussions" in the sidebar navigation (Flarum 2
  // exposes these via navItems, not items — items is the New Discussion button +
  // the nav dropdown itself). Admins can hide it — the widgets link to /calendar
  // too, so some forums prefer an uncluttered nav.
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    if (app.forum.attribute('ernestdefoe-calendar.hideNavLink')) return;
    items.add('calendar', LinkButton.component({
      href: app.route('calendar'),
      icon: 'fas fa-calendar-days'
    }, app.translator.trans('ernestdefoe-calendar.forum.nav')), 5);
  });

  // With fof/forum-widgets-core enabled, every calendar widget is a managed
  // widget (registered below) — the admin places each one there, so the
  // hardcoded sidebar mounts step aside to avoid showing anything twice.
  const hasWidgetCore = hasFofWidgets();

  // Stock-theme widgets, each its own sidebar block below the navigation.
  extend(IndexSidebar.prototype, 'items', function (items) {
    /*
     * 🚨 The INDEX only, and that is not an optimisation.
     *
     * Core renders this list as `<li>`s inside the navigation's own `<ul>`:
     *
     *     <nav class="IndexPage-nav sideNav"><ul>{items}</ul></nav>
     *
     * On the index, where that nav is a vertical column, a widget in there
     * happens to look like a block underneath it. `IndexSidebar` is reused by
     * other pages though — the Hashtags page is one — and those lay the same
     * `<ul>` out HORIZONTALLY. The widgets are then dragged into the nav row,
     * turning "Upcoming events" and the pulse chart into nav entries that
     * overflow the bar and force a horizontal scrollbar across the page.
     *
     * They are widgets, so they render where widgets render and nowhere else.
     * Anything wanting them on another page should use the FoF widget
     * registration below, where an admin places them deliberately.
     */
    if (!app.current || !app.current.matches(IndexPage) || hasWidgetCore) return;
    if (app.forum.attribute('ernestdefoe-calendar.showIndexWidget')) {
      const count = app.forum.attribute('ernestdefoe-calendar.indexWidgetCount') || 5;
      items.add('calendar-upcoming', m(UpcomingEvents, {
        count
      }), -10);
    }
    if (app.forum.attribute('ernestdefoe-calendar.showPulseWidget')) {
      items.add('calendar-pulse', m(PulseWidget, {
        count: 5
      }), -12);
    }
    if (app.forum.attribute('ernestdefoe-calendar.showCelebrationsWidget')) {
      items.add('calendar-celebrations', m(CelebrationsWidget), -13);
    }
    if (app.forum.attribute('ernestdefoe-calendar.showMemoriesWidget')) {
      items.add('calendar-memories', m(OnThisDayWidget, {
        count: 6
      }), -14);
    }
  });

  // Self-service birthday picker in the account settings page (opt-in, MM-DD only).
  // SettingsPage is a lazily loaded chunk in Flarum 2, so it is extended by path:
  // its route component is an async loader with no prototype to extend.
  extend('flarum/forum/components/SettingsPage', 'settingsItems', function (items) {
    items.add('calendarBirthday', FieldSet.component({
      className: 'Settings-calendarBirthday',
      label: app.translator.trans('ernestdefoe-calendar.forum.birthday_section')
    }, [m(BirthdayField)]), 5);
  });

  /*
   * A contribution heatmap + streaks at the top of every member's profile.
   *
   * 🚨 Stands down when ernestdefoe/cadence is drawing one, or a profile shows
   * TWO activity maps stacked on each other. Cadence is the dedicated one —
   * absolute scale, activity by kind, the reader's own timezone — and this one
   * stays for the forums that have only ever had Calendar.
   *
   * The check is deliberately narrow: Cadence being INSTALLED is not enough,
   * because its profile placement is a setting an admin can turn off. If it is
   * off, Calendar keeps drawing, and nobody ends up with no map at all.
   */
  override(PostsUserPage.prototype, 'content', function (original) {
    const cadenceIsDrawing = 'ernestdefoe-cadence' in (window.flarum?.extensions ?? {}) && app.forum.attribute('cadenceShowOnProfile') !== false;
    const heatmap = this.user && !cadenceIsDrawing ? m('.PostsUserPage-heatmap', m(ActivityHeatmap, {
      userId: this.user.id()
    })) : null;
    return [heatmap, original()];
  });

  // fof/forum-widgets-core (optional): register each widget as a managed
  // widget. Its modules live in the main bundle, so this is safe at init time.
  registerFofWidget(app, {
    calendarUpcoming: () => m(UpcomingEvents, {
      count: app.forum.attribute('ernestdefoe-calendar.indexWidgetCount') || 5
    }),
    calendarPulse: () => m(PulseWidget, {
      count: 5
    }),
    calendarCelebrations: () => m(CelebrationsWidget),
    calendarMemories: () => m(OnThisDayWidget, {
      count: 6
    })
  }, {
    // Celebrations draws nothing on a day with nobody to celebrate; until the
    // day's answer is in, it shows its skeleton (and that is what asks).
    calendarCelebrations: () => {
      const people = peek('celebrations');
      return !(Array.isArray(people) && people.length === 0);
    }
  });

  // Defer so sibling extensions (Bespoke / Page Builder) have run their own
  // initializers and created app.bespoke / app.pageBuilder before we register our
  // widgets into them — cross-extension initializer order isn't guaranteed, and a
  // macrotask runs after Flarum's synchronous boot completes.
  setTimeout(registerIntegrations, 0);

  // Turn [countdown=…] placeholders (and event-modal countdowns) into live timers.
  startCountdowns();
});
export { default as UpcomingEvents } from './components/UpcomingEvents';flarum.reg.addChunkModule('ernestdefoe-calendar:forum/components/CalendarPage', '623', 'ernestdefoe-calendar', 'forum/components/CalendarPage');
flarum.reg.addChunkModule('ernestdefoe-calendar:forum/components/CalendarPage', '623', 'ernestdefoe-calendar', 'forum/components/MonthGrid');
flarum.reg.addChunkModule('ernestdefoe-calendar:forum/components/CalendarPage', '623', 'ernestdefoe-calendar', 'forum/components/TimeGrid');
flarum.reg.addChunkModule('ernestdefoe-calendar:forum/components/CalendarPage', '623', 'ernestdefoe-calendar', 'forum/components/EventListView');